import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { format } from "date-fns";
import {
  Search,
  Package,
  Users,
  ShoppingCart,
  TrendingUp,
  RefreshCw,
  Plus,
  Printer,
  Calendar,
  PlusCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDashboardData } from "@/hooks/use-dashboard";
import { fmtDateTime } from "@/lib/format";
import { TopCustomersCard } from "./top-customer";
import { RecentOrdersCard } from "./recent-orders";
import { LowStockProductsCard } from "./low-stock";
import { useLocation } from "wouter";
import { ChartDataItem, DashboardChart } from "./dashboard-chart";


// Skeletons (tiny, reusable)
const KpiSkeleton = () => (
  <Card className="hover:shadow-md transition">
    <CardHeader className="pb-2">
      <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
    </CardHeader>
    <CardContent>
      <div className="h-7 w-24 rounded bg-gray-200 animate-pulse" />
    </CardContent>
  </Card>
);

const ChartSkeleton = () => (
  <Card>
    <CardHeader className="pb-0">
      <div className="h-4 w-60 rounded bg-gray-200 animate-pulse" />
    </CardHeader>
    <CardContent className="pt-4">
      <div className="h-52 w-full rounded bg-gray-100 animate-pulse" />
    </CardContent>
  </Card>
);

const TableSkeleton = ({
  rows = 6,
  cols = 6,
}: {
  rows?: number;
  cols?: number;
}) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="h-4 w-48 rounded bg-gray-200 animate-pulse" />
    </CardHeader>
    <CardContent>
      <div className="overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {Array.from({ length: cols }).map((_, i) => (
                <th key={i} className="px-3 py-2 text-left">
                  <div className="h-3 w-24 rounded bg-gray-200 animate-pulse" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r} className="border-b">
                {Array.from({ length: cols }).map((_, c) => (
                  <td key={c} className="px-3 py-3">
                    <div className="h-3 w-full max-w-[160px] rounded bg-gray-100 animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);





// Helpers
const currency = (n: number, cur = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(
    n
  );

const StatusBadge = ({ s }: { s: string }) => {
  const styles: Record<string, string> = {
    PAID: "bg-green-100 text-green-700",
    PENDING: "bg-yellow-100 text-yellow-700",
    SHIPPING: "bg-blue-100 text-blue-700",
    CANCELLED: "bg-red-100 text-red-700",
  };
  return (
    <Badge
      className={`rounded-full ${styles[s] ?? "bg-gray-100 text-gray-700"}`}
    >
      {s}
    </Badge>
  );
};

// ---- Dashboard -------------------------------------------------------------
const MiniPOSDashboard: React.FC = () => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string | "ALL">("ALL");
  const [, setLocation] = useLocation();
  const { data, errors, loading, refresh, reloadConversation } = useDashboardData();

  const chartData = useMemo(() => {
    if (!data.last7DaysSale) return [];
    return data.last7DaysSale.map((item) => ({
      day: format(new Date(item.summaryDate), "EEE"),
      orders: item.totalOrders,
      revenue: item.totalSaleRevenue,
    })) as ChartDataItem[];
  }, [data.last7DaysSale]);
  const handleNewOrder = () => {
    setLocation("/orders/new");
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
          Dashboard
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={reloadConversation}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Reloading" : "Conversation"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="hover:shadow-md transition">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Revenue Today
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {currency(data.revenueToday || 0)}
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Pending Orders
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {data.pendingOrders || 0}
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Customers
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {data.totalCustomer || 0}
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Low Stock
              </CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold">
              {data.totalLowStock || 0}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sales Chart */}
      {loading ? (
        <ChartSkeleton />
      ) : (
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Sales (Last 7 days)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            {/* REMOVE THE "h-52" DIV WRAPPER HERE */}
            <DashboardChart chartData={chartData} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* Orders */}
        {loading ? (
          <div className="xl:col-span-7">
            <TableSkeleton rows={6} cols={7} />
          </div>
        ) : (
          <RecentOrdersCard
            orders={data.recentOrders || []}
            loading={loading}
          />
        )}

        {/* Stocks */}
        {loading ? (
          <div className="xl:col-span-5">
            <TableSkeleton rows={6} cols={5} />
          </div>
        ) : (
          <LowStockProductsCard products={data.lowStockProducts || []} />
        )}
      </div>

      {/* Customers */}
      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <TopCustomersCard
          customers={data?.topCustomers || []}
          loading={loading}
          onView={() => { }}
        />
      )}
    </div>
  );
};

export default MiniPOSDashboard;
